import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, IconButton,
  TextField, Chip, CircularProgress, Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FilterListIcon from '@material-ui/icons/FilterList';
import axios from 'axios';

const DISCIPLINE_COLORS = {
  landscape: '#4caf50',
  irrigation: '#2196f3',
  hardscape: '#795548',
  grading: '#ff9800',
  lighting: '#ffc107',
  demolition: '#f44336',
  detail: '#9c27b0',
  planting: '#66bb6a',
  cover: '#607d8b',
  notes: '#9e9e9e',
  tree_inventory: '#2e7d32',
  other: '#bdbdbd',
};

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: 800,
    width: '90vw',
    maxHeight: '85vh',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  content: {
    padding: '16px 20px',
    overflowY: 'auto',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  filterChip: {
    fontSize: 11,
    height: 26,
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  pageCard: {
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#999',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  },
  pageCardSelected: {
    borderColor: '#4caf50',
    background: '#f1f8e9',
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
  },
  thumbnail: {
    width: '100%',
    height: 160,
    objectFit: 'contain',
    background: '#fafafa',
    display: 'block',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    color: '#bbb',
    fontSize: 12,
  },
  pageInfo: {
    padding: '8px 10px',
    borderTop: '1px solid #eee',
  },
  pageNumber: {
    fontSize: 11,
    fontWeight: 600,
    color: '#333',
  },
  pageTitle: {
    fontSize: 11,
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: 2,
  },
  disciplineBadge: {
    fontSize: 9,
    fontWeight: 600,
    color: '#fff',
    padding: '1px 6px',
    borderRadius: 3,
    display: 'inline-block',
    marginTop: 4,
    textTransform: 'uppercase',
  },
  selectedCheck: {
    position: 'absolute',
    top: 6,
    right: 6,
    color: '#4caf50',
    background: '#fff',
    borderRadius: '50%',
  },
  cardWrapper: {
    position: 'relative',
  },
  emptyState: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
  },
  projectPicker: {
    marginBottom: 16,
    padding: 12,
    background: '#f5f5f5',
    borderRadius: 8,
  },
  actions: {
    padding: '12px 20px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

/**
 * Modal for picking plan pages to add as map overlays.
 *
 * Props:
 *   open, onClose
 *   url - API base URL
 *   jobsite - current jobsite object
 *   onAddPages(pages[]) - callback with selected page objects
 */
const PlanPagePickerModal = ({ open, onClose, url, jobsite, onAddPages }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [disciplineFilter, setDisciplineFilter] = useState(null);

  // Fetch available projects with plan pages for this jobsite
  useEffect(() => {
    if (!open || !url || !jobsite?._id) return;
    setSelectedPageIds(new Set());
    setDisciplineFilter(null);
    setLoading(true);
    axios
      .get(`${url}/api/bid-estimation/plan-pages`, {
        params: { jobSiteId: jobsite._id },
      })
      .then((resp) => {
        const data = resp.data || {};
        setProjects(data.projects || []);
        // Auto-select first project if only one
        if (data.projects?.length === 1) {
          setSelectedProjectId(data.projects[0]._id);
          setPages(data.projects[0].pages || []);
        } else if (data.projects?.length > 1) {
          setSelectedProjectId(null);
          setPages([]);
        } else {
          setPages([]);
        }
      })
      .catch(() => {
        setProjects([]);
        setPages([]);
      })
      .finally(() => setLoading(false));
  }, [open, url, jobsite]);

  const handleSelectProject = (projId) => {
    setSelectedProjectId(projId);
    const proj = projects.find((p) => p._id === projId);
    setPages(proj?.pages || []);
    setSelectedPageIds(new Set());
    setDisciplineFilter(null);
  };

  const togglePage = (pageId) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPageIds(new Set(filteredPages.map((p) => p.id)));
  };

  const selectNone = () => {
    setSelectedPageIds(new Set());
  };

  // Get unique disciplines for filter chips
  const disciplines = useMemo(() => {
    const set = new Set(pages.map((p) => p.discipline).filter(Boolean));
    return Array.from(set).sort();
  }, [pages]);

  const filteredPages = useMemo(() => {
    if (!disciplineFilter) return pages;
    return pages.filter((p) => p.discipline === disciplineFilter);
  }, [pages, disciplineFilter]);

  const handleAdd = () => {
    const selected = pages.filter((p) => selectedPageIds.has(p.id));
    if (selected.length > 0 && onAddPages) {
      onAddPages(selected);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <div className={classes.title}>
        <Typography style={{ fontSize: 16, fontWeight: 500 }}>
          Add Plan Pages as Overlays
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.content}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && projects.length === 0 && (
          <div className={classes.emptyState}>
            <Typography variant="body1" style={{ fontWeight: 500, marginBottom: 8 }}>
              No plan pages available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload a plan set PDF in Bid Estimation to make pages available here.
              Once uploaded, each page will be rendered as an image you can overlay on the map.
            </Typography>
          </div>
        )}

        {/* Project picker (if multiple projects linked) */}
        {!loading && projects.length > 1 && (
          <div className={classes.projectPicker}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#888' }}>
              Select a project:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              {projects.map((proj) => (
                <Chip
                  key={proj._id}
                  label={`${proj.name} (${proj.pages?.length || 0} pages)`}
                  onClick={() => handleSelectProject(proj._id)}
                  color={selectedProjectId === proj._id ? 'primary' : 'default'}
                  variant={selectedProjectId === proj._id ? 'default' : 'outlined'}
                  size="small"
                />
              ))}
            </Box>
          </div>
        )}

        {/* Discipline filter + select all/none */}
        {pages.length > 0 && (
          <div className={classes.toolbar}>
            <FilterListIcon style={{ fontSize: 16, color: '#999' }} />
            <Chip
              label="All"
              size="small"
              className={classes.filterChip}
              variant={!disciplineFilter ? 'default' : 'outlined'}
              color={!disciplineFilter ? 'primary' : 'default'}
              onClick={() => setDisciplineFilter(null)}
            />
            {disciplines.map((d) => (
              <Chip
                key={d}
                label={d}
                size="small"
                className={classes.filterChip}
                variant={disciplineFilter === d ? 'default' : 'outlined'}
                style={
                  disciplineFilter === d
                    ? { background: DISCIPLINE_COLORS[d] || '#999', color: '#fff' }
                    : {}
                }
                onClick={() => setDisciplineFilter(disciplineFilter === d ? null : d)}
              />
            ))}
            <Box flex={1} />
            <Button size="small" onClick={selectAll} style={{ fontSize: 11, textTransform: 'none' }}>
              Select all
            </Button>
            <Button size="small" onClick={selectNone} style={{ fontSize: 11, textTransform: 'none' }}>
              Clear
            </Button>
          </div>
        )}

        {/* Page grid */}
        {filteredPages.length > 0 && (
          <div className={classes.grid}>
            {filteredPages.map((page) => {
              const isSelected = selectedPageIds.has(page.id);
              return (
                <div key={page.id} className={classes.cardWrapper}>
                  <div
                    className={`${classes.pageCard} ${isSelected ? classes.pageCardSelected : ''}`}
                    onClick={() => togglePage(page.id)}
                  >
                    {page.thumbnailUrl ? (
                      <img
                        src={page.thumbnailUrl}
                        alt={`Page ${page.pageNumber}`}
                        className={classes.thumbnail}
                      />
                    ) : (
                      <div className={classes.thumbnailPlaceholder}>
                        Page {page.pageNumber}
                      </div>
                    )}
                    <div className={classes.pageInfo}>
                      <div className={classes.pageNumber}>
                        {page.sheetNumber || `Page ${page.pageNumber}`}
                      </div>
                      {page.title && (
                        <div className={classes.pageTitle}>{page.title}</div>
                      )}
                      {page.discipline && (
                        <span
                          className={classes.disciplineBadge}
                          style={{ background: DISCIPLINE_COLORS[page.discipline] || '#999' }}
                        >
                          {page.discipline}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircleIcon className={classes.selectedCheck} style={{ fontSize: 22 }} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={classes.actions}>
        <Typography variant="caption" color="textSecondary">
          {selectedPageIds.size > 0
            ? `${selectedPageIds.size} page${selectedPageIds.size > 1 ? 's' : ''} selected`
            : 'Select pages to overlay on map'}
        </Typography>
        <Box display="flex" gap={1}>
          <Button onClick={onClose} size="small" style={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            size="small"
            variant="contained"
            color="primary"
            disabled={selectedPageIds.size === 0}
            style={{ textTransform: 'none' }}
          >
            Add {selectedPageIds.size > 0 ? `${selectedPageIds.size} ` : ''}to map
          </Button>
        </Box>
      </div>
    </Dialog>
  );
};

export default PlanPagePickerModal;
